/**
 * FailureRecoveryModal 组件
 * 
 * 失败恢复弹窗，提供重试和切换手动模式选项
 * 
 * @module components/projectSettings/FailureRecoveryModal
 * @see Requirements 6.6, 6.7
 */

import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import {
  GenerationStepType,
  GENERATION_STEP_NAMES
} from '@packages/global';
import { useTranslation } from 'react-i18next';

interface FailureRecoveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  failedStep: GenerationStepType;
  errorMessage: string;
  onRetry: () => void;
  onSwitchToManual: () => void;
  isLoading?: boolean;
}

/**
 * FailureRecoveryModal 组件
 */
export default function FailureRecoveryModal({
  isOpen,
  onClose,
  failedStep,
  errorMessage,
  onRetry,
  onSwitchToManual,
  isLoading = false
}: FailureRecoveryModalProps) {
  const { t } = useTranslation(['projectSettings', 'common']);
  const stepName = GENERATION_STEP_NAMES[failedStep];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* 背景遮罩 */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle p-6 shadow-xl transition-all">
                {/* 关闭按钮 */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-50 transition-colors cursor-pointer"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>

                {/* 图标 */}
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-error/20 rounded-full">
                    <ExclamationTriangleIcon className="w-8 h-8 text-error" />
                  </div>
                </div>

                {/* 标题 */}
                <Dialog.Title className="text-lg font-semibold text-gray-50 text-center mb-2">
                  {t('failureRecovery.generationFailed')}
                </Dialog.Title>

                {/* 错误信息 */}
                <div className="bg-bg-page rounded-lg border border-border-subtle p-4 mb-6">
                  <p className="text-sm text-gray-300 mb-2">
                    {t('failureRecovery.stepFailed', { stepName })}
                  </p>
                  <p className="text-xs text-gray-400">{errorMessage}</p>
                </div>

                {/* 操作按钮 */}
                <div className="space-y-3">
                  {/* 重试按钮 */}
                  <button
                    onClick={onRetry}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3
                      bg-primary hover:bg-primary-dark 
                      text-white rounded-lg text-sm font-medium 
                      transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <ArrowPathIcon className="w-4 h-4" />
                    )}
                    {t('failureRecovery.retryCurrentStep')}
                  </button>

                  {/* 切换手动模式按钮 */}
                  <button
                    onClick={onSwitchToManual}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3
                      bg-bg-elevated hover:bg-bg-surface 
                      text-gray-50 rounded-lg text-sm font-medium 
                      transition-colors border border-border-default
                      disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                    {t('failureRecovery.switchToManual')}
                  </button>
                </div>

                {/* 提示信息 */}
                <div className="mt-4 pt-4 border-t border-border-subtle">
                  <p className="text-xs text-gray-400 text-center">
                    {t('failureRecovery.manualModeHint')}
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
